import type { IFilesystem } from '@livx.cc/wcli/core';
import { OverlayFilesystem } from './OverlayFilesystem';

/**
 * Speculative parallel attempts — a primitive uniquely cheap on a SWAPPABLE VFS.
 *
 * Fork the workspace into N copy-on-write overlays of the same `base`, run an attempt in
 * each (independently — writes stay in each overlay, the base is untouched), score them,
 * then COMMIT the best overlay into the base and DISCARD the losers (they leave no trace).
 * Use it to race several approaches/seeds for a flaky or multi-path task and keep only the
 * winner — something agents on a real, non-forkable filesystem can't do without copying the
 * whole tree. Pairs naturally with subagents (each attempt is a child Agent over its overlay).
 *
 * `score` returns a higher-is-better number, or null to disqualify (e.g. failed grading).
 */
export interface Attempt<R> {
  index: number;
  fs: OverlayFilesystem;
  result: R;
  score: number | null;
}

export async function raceAttempts<R>(
  base: IFilesystem,
  n: number,
  run: (fs: IFilesystem, index: number) => Promise<R>,
  score: (a: { fs: OverlayFilesystem; result: R; index: number }) => Promise<number | null> | number | null,
): Promise<{ winner: Attempt<R> | null; attempts: Attempt<R>[] }> {
  const attempts: Attempt<R>[] = await Promise.all(
    Array.from({ length: Math.max(1, n) }, async (_, index) => {
      const fs = new OverlayFilesystem(base);
      const result = await run(fs, index);
      const s = await score({ fs, result, index });
      return { index, fs, result, score: s };
    }),
  );
  // Best non-disqualified score wins; ties broken by earliest index (stable).
  const ranked = attempts.filter((a) => a.score != null).sort((a, b) => b.score! - a.score! || a.index - b.index);
  const winner = ranked[0] ?? null;
  if (winner) await winner.fs.commit(); // apply ONLY the winner to the base; losers are discarded
  return { winner, attempts };
}
